import {
  Check,
  ChevronDown,
  Copy,
  Download,
  ExternalLink,
  Eye,
  FolderOpen,
  Layers,
  Pencil,
  Plus,
  Share2,
  Trash2,
  Upload,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { scenarioHref } from '../lib/tab';
import { relativeTime } from '../lib/utils';
import { useAppStore } from '../store/useAppStore';
import { Button, Field, IconButton, Input, Modal } from './ui';

export function ScenarioMenu() {
  const scenario = useAppStore((s) => s.scenario);
  const scenarioList = useAppStore((s) => s.scenarioList);
  const summary = useAppStore((s) => s.summary);
  const selectScenario = useAppStore((s) => s.selectScenario);
  const newScenario = useAppStore((s) => s.newScenario);
  const renameScenario = useAppStore((s) => s.renameScenario);
  const duplicateScenario = useAppStore((s) => s.duplicateScenario);
  const deleteScenario = useAppStore((s) => s.deleteScenario);
  const role = useAppStore((s) => s.role);
  const openFromDrive = useAppStore((s) => s.openFromDrive);
  const importScenario = useAppStore((s) => s.importScenario);
  const exportScenario = useAppStore((s) => s.exportScenario);
  // Both: without the project number a pick grants this app nothing, so the
  // button would open a dialog that cannot deliver what it offers.
  const canPick = useAppStore((s) => s.pickerApiKey !== null && s.pickerAppId !== null);
  const readOnly = role === 'reader';

  const fileRef = useRef<HTMLInputElement>(null);
  const onPickFile = async (file: File) => {
    try {
      await importScenario(JSON.parse(await file.text()));
    } catch {
      useAppStore.getState().toast('error', 'That file is not valid JSON.');
    }
  };

  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [draftNotes, setDraftNotes] = useState('');

  /*
   * Fetched when the menu opens rather than on the click, and rendered as a
   * plain link. Asking Drive for the URL and *then* calling window.open puts a
   * network round trip between the click and the popup, which is exactly what
   * a popup blocker exists to stop — the same lesson as the Drive consent.
   *
   * Null means scenarios are files on disk, which is every local run, so the
   * button is simply not there rather than being there and apologising.
   */
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const scenarioId = scenario?.id ?? null;
  useEffect(() => {
    if (!open || !scenarioId) return;
    let live = true;
    void api
      .shareLink(scenarioId)
      .then((result) => {
        if (live) setShareUrl(result.url);
      })
      .catch(() => {
        if (live) setShareUrl(null);
      });
    return () => {
      live = false;
    };
  }, [open, scenarioId]);

  return (
    <>
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          className="flex h-9 w-full items-center gap-2 rounded-lg border border-[var(--color-line)] bg-[var(--color-surface)] px-2.5 text-[12.5px] font-medium transition-colors hover:bg-[var(--color-surface-2)] sm:w-auto sm:max-w-[180px]"
        >
          <Layers size={14} className="shrink-0 text-[var(--color-brand)]" />
          <span className="truncate text-[var(--color-ink)]">{scenario?.name ?? 'Scenario'}</span>
          {/* Before the first edit, not after one is refused. */}
          {readOnly ? (
            <Eye size={12} aria-label="View only" className="shrink-0 text-[var(--color-ink-3)]" />
          ) : null}
          {summary.total > 0 ? (
            <span className="shrink-0 rounded-full bg-[var(--color-brand-soft)] px-1.5 text-[10px] font-bold text-[var(--color-brand)]">
              {summary.total}
            </span>
          ) : null}
          <ChevronDown size={13} className="shrink-0 text-[var(--color-ink-3)]" />
        </button>

        {open ? (
          <>
            <button
              type="button"
              aria-label="Close menu"
              className="fixed inset-0 z-10 cursor-default"
              onClick={() => setOpen(false)}
            />
            <div className="absolute top-11 left-0 z-20 w-[min(20rem,calc(100vw-1.5rem))] rounded-xl border border-[var(--color-line)] bg-[var(--color-surface)] p-1.5 shadow-[var(--shadow-panel)]">
              <p className="px-2 pt-1.5 pb-2 text-[10.5px] font-semibold tracking-wide text-[var(--color-ink-3)] uppercase">
                Scenarios
              </p>
              <div className="max-h-72 space-y-0.5 overflow-y-auto">
                {scenarioList.map((item) => (
                  <div
                    key={item.id}
                    className="group flex items-center gap-2 rounded-lg pr-1 transition-colors hover:bg-[var(--color-surface-2)]"
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setOpen(false);
                        if (item.id !== scenario?.id) void selectScenario(item.id);
                      }}
                      className="flex min-w-0 flex-1 items-center gap-2 px-2 py-2 text-left"
                    >
                      <span className="flex size-4 shrink-0 items-center justify-center">
                        {item.id === scenario?.id ? (
                          <Check size={14} className="text-[var(--color-brand)]" />
                        ) : null}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[12.5px] font-medium text-[var(--color-ink)]">
                          {item.name}
                        </span>
                        <span className="block truncate text-[10.5px] text-[var(--color-ink-3)]">
                          {item.changeCount === 0
                            ? 'No changes'
                            : `${item.changeCount} change${item.changeCount === 1 ? '' : 's'}`}
                          {' · '}
                          {relativeTime(item.updatedAt)}
                        </span>
                      </span>
                    </button>
                    {/* Two scenarios side by side is the point of pinning a
                        tab to one, so make opening a second one one click. */}
                    <a
                      href={scenarioHref(item.id)}
                      target="_blank"
                      rel="noreferrer"
                      title={`Open ${item.name} in a new tab`}
                      aria-label={`Open ${item.name} in a new tab`}
                      onClick={(event) => event.stopPropagation()}
                      className="flex size-6 shrink-0 items-center justify-center rounded-lg text-[var(--color-ink-3)] transition-colors hover:bg-[var(--color-surface-2)] hover:text-[var(--color-brand)]"
                    >
                      <ExternalLink size={12} />
                    </a>
                    {/*
                     * Always shown, and never disabled. Revealing these on hover
                     * made them impossible to find. The last one used to be
                     * undeletable because there had to be a scenario to land on;
                     * now there is somewhere to land — the first-run screen.
                     */}
                    <IconButton
                      label={`Delete ${item.name}`}
                      className="size-6 hover:text-[var(--color-moved)]"
                      onClick={() => {
                        if (window.confirm(`Delete “${item.name}”? This cannot be undone.`)) {
                          void deleteScenario(item.id);
                        }
                      }}
                    >
                      <Trash2 size={12} />
                    </IconButton>
                  </div>
                ))}
              </div>

              <div className="mt-1.5 flex items-center gap-1 border-t border-[var(--color-line)] pt-1.5">
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex-1 justify-start"
                  onClick={() => {
                    setOpen(false);
                    setDraftName('');
                    setDraftNotes('');
                    setCreating(true);
                  }}
                >
                  <Plus size={13} /> New
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex-1 justify-start"
                  disabled={readOnly}
                  title={readOnly ? 'This plan was shared with you to read' : undefined}
                  onClick={() => {
                    setOpen(false);
                    setDraftName(scenario?.name ?? '');
                    setDraftNotes(scenario?.description ?? '');
                    setRenaming(true);
                  }}
                >
                  <Pencil size={13} /> Rename
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex-1 justify-start"
                  onClick={() => {
                    setOpen(false);
                    void duplicateScenario();
                  }}
                >
                  <Copy size={13} /> Duplicate
                </Button>
              </div>

              {/*
               * Sharing is Drive's dialog, not ours. A scenario is one file in
               * the author's own Drive, and Drive already knows how to give one
               * person access to one file — inventing a second sharing model
               * here would mean the app deciding who may read a plan, which is
               * the decision the storage design deliberately left with them.
               */}
              {/*
               * The only way in to a plan somebody shared. `drive.file` cannot
               * see a file until it has been picked, so a scenario shared with
               * you is absent from the list above until you choose it here —
               * this is not a nicer way to type an id, it is the act that turns
               * the share into access.
               */}
              {canPick ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-1.5 w-full justify-start border-t border-[var(--color-line)] pt-1.5"
                  onClick={() => {
                    setOpen(false);
                    void openFromDrive();
                  }}
                >
                  <FolderOpen size={13} /> Open from Drive…
                </Button>
              ) : null}

              {/*
               * A scenario as a file, in and out. They live here rather than in
               * the toolbar because they are two more ways of getting a plan —
               * beside New, Duplicate and Open from Drive, which is where
               * somebody goes looking for one. In the toolbar they were two
               * unlabelled arrows among the view controls, and the arrows are
               * the only thing that told import from export.
               */}
              <div className="mt-1.5 flex items-center gap-1 border-t border-[var(--color-line)] pt-1.5">
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex-1 justify-start"
                  onClick={() => {
                    setOpen(false);
                    fileRef.current?.click();
                  }}
                >
                  <Upload size={13} /> Import…
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex-1 justify-start"
                  disabled={!scenario}
                  onClick={() => {
                    setOpen(false);
                    void exportScenario();
                  }}
                >
                  <Download size={13} /> Export
                </Button>
              </div>

              {shareUrl ? (
                <a
                  href={shareUrl}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => setOpen(false)}
                  className="mt-1.5 flex items-center gap-1.5 rounded-lg border-t border-[var(--color-line)] px-2 py-2 text-[12px] text-[var(--color-ink-2)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-brand)]"
                >
                  <Share2 size={13} className="shrink-0" />
                  Share in Drive
                  <ExternalLink size={11} className="ml-auto shrink-0 opacity-60" />
                </a>
              ) : null}
            </div>
          </>
        ) : null}
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="application/json,.json"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void onPickFile(file);
          event.target.value = '';
        }}
      />

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title="New scenario"
        description="A scenario is a set of proposed changes layered on top of today's org."
        footer={
          <>
            <Button variant="ghost" onClick={() => setCreating(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={draftName.trim().length === 0}
              onClick={() => {
                void newScenario(draftName.trim(), draftNotes.trim());
                setCreating(false);
              }}
            >
              Create
            </Button>
          </>
        }
      >
        <Field label="Name">
          <Input
            autoFocus
            value={draftName}
            placeholder="e.g. Q4 platform reorg"
            onChange={(event) => setDraftName(event.target.value)}
          />
        </Field>
        <Field label="Notes" hint="Optional — what you're trying to test with this scenario.">
          <Input value={draftNotes} onChange={(event) => setDraftNotes(event.target.value)} />
        </Field>
      </Modal>

      <Modal
        open={renaming}
        onClose={() => setRenaming(false)}
        title="Rename scenario"
        footer={
          <>
            <Button variant="ghost" onClick={() => setRenaming(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={draftName.trim().length === 0}
              onClick={() => {
                void renameScenario(draftName.trim(), draftNotes);
                setRenaming(false);
              }}
            >
              Save
            </Button>
          </>
        }
      >
        <Field label="Name">
          <Input autoFocus value={draftName} onChange={(event) => setDraftName(event.target.value)} />
        </Field>
        <Field label="Notes">
          <Input value={draftNotes} onChange={(event) => setDraftNotes(event.target.value)} />
        </Field>
      </Modal>
    </>
  );
}
